import React, { useEffect, useState } from 'react';
import { 
    Card, 
    CardContent, 
    Typography, 
    Grid, 
    Button, 
    Box,
    Chip,
    Alert,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Pagination,
    IconButton,
    Tooltip,
    Avatar,
    Fab
} from '@mui/material';
import { 
    CheckCircle, 
    Error, 
    Pending, 
    PlayArrow, 
    CleaningServices,
    Delete,
    Refresh,
    AccessTime,
    Add,
    Visibility
} from '@mui/icons-material';
import { getTaskQueueStatus, cleanupTaskQueue, getTasks, deleteTask, retryTask } from '../Api';
import TaskQueueStatusDto from '../models/dtos/TaskQueueStatusDto';
import FlashBetTaskDetailDto, { PageFlashBetTaskDetailDto } from '../models/dtos/FlashBetTaskDetailDto';
import AddTaskModal from '../components/AddTaskModal';
import ViewMatchModal from '../components/ViewMatchModal';
import { extractErrorMessage, createUserFriendlyErrorMessage } from '../utils/ErrorUtil';

interface TimeRange {
    label: string;
    hours: number;
}

const TIME_RANGES: TimeRange[] = [
    { label: '1 Hour', hours: 1 },
    { label: '6 Hours', hours: 6 },
    { label: '24 Hours', hours: 24 },
    { label: '72 Hours', hours: 72 },
    { label: '1 Week', hours: 168 },
    { label: '1 Month', hours: 720 }
];

const FlashBetTasksPage: React.FC = () => {
    const [queueStatus, setQueueStatus] = useState<TaskQueueStatusDto | null>(null);
    const [tasks, setTasks] = useState<PageFlashBetTaskDetailDto | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [tasksLoading, setTasksLoading] = useState<boolean>(true);
    const [cleanupLoading, setCleanupLoading] = useState<boolean>(false);
    const [timeRange, setTimeRange] = useState<number>(24);
    const [currentPage, setCurrentPage] = useState<number>(0);
    const [pageSize] = useState<number>(10);
    const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
    const [alertMessage, setAlertMessage] = useState<string>('');
    const [alertSeverity, setAlertSeverity] = useState<'success' | 'error' | 'info'>('info');
    const [actionLoading, setActionLoading] = useState<{ [key: string]: boolean }>({});
    const [addTaskModalOpen, setAddTaskModalOpen] = useState<boolean>(false);
    const [viewMatchModalOpen, setViewMatchModalOpen] = useState<boolean>(false);
    const [selectedMatchId, setSelectedMatchId] = useState<string | null>(null);

    const loadQueueStatus = async () => {
        setLoading(true);
        try {
            const status = await getTaskQueueStatus(timeRange);
            if (status) {
                setQueueStatus(status);
                setLastUpdated(new Date());
            } else {
                setAlertMessage('Failed to load task queue status');
                setAlertSeverity('error');
            }
        } catch (error) {
            console.error('Error loading queue status:', error);
            const errorMsg = await createUserFriendlyErrorMessage('load task queue status', error);
            setAlertMessage(errorMsg);
            setAlertSeverity('error');
        } finally {
            setLoading(false);
        }
    };

    const loadTasks = async () => {
        setTasksLoading(true);
        try {
            const tasksData = await getTasks(currentPage, pageSize, timeRange);
            if (tasksData) {
                setTasks(tasksData);
            } else {
                setAlertMessage('Failed to load tasks');
                setAlertSeverity('error');
            }
        } catch (error) {
            console.error('Error loading tasks:', error);
            const errorMsg = await createUserFriendlyErrorMessage('load tasks', error);
            setAlertMessage(errorMsg);
            setAlertSeverity('error');
        } finally {
            setTasksLoading(false);
        }
    };

    const handleTimeRangeChange = (newRange: number) => {
        setTimeRange(newRange);
        setCurrentPage(0);
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setCurrentPage(value - 1);
    };

    const handleDeleteTask = async (taskId: string) => {
        setActionLoading({ ...actionLoading, [taskId]: true });
        try {
            const success = await deleteTask(taskId);
            if (success) {
                setAlertMessage('Task deleted successfully');
                setAlertSeverity('success');
                await Promise.all([loadQueueStatus(), loadTasks()]);
            } else {
                setAlertMessage('Failed to delete task');
                setAlertSeverity('error');
            }
        } catch (error) {
            console.error('Error deleting task:', error);
            const errorMsg = await createUserFriendlyErrorMessage('delete task', error);
            setAlertMessage(errorMsg);
            setAlertSeverity('error');
        } finally {
            setActionLoading({ ...actionLoading, [taskId]: false });
        }
    };

    const handleRetryTask = async (taskId: string) => {
        setActionLoading({ ...actionLoading, [taskId]: true });
        try {
            const result = await retryTask(taskId);
            if (result) {
                setAlertMessage('Task retry initiated successfully');
                setAlertSeverity('success');
                await Promise.all([loadQueueStatus(), loadTasks()]);
            } else {
                setAlertMessage('Failed to retry task');
                setAlertSeverity('error');
            }
        } catch (error) {
            console.error('Error retrying task:', error);
            const errorMsg = await createUserFriendlyErrorMessage('retry task', error);
            setAlertMessage(errorMsg);
            setAlertSeverity('error');
        } finally {
            setActionLoading({ ...actionLoading, [taskId]: false });
        }
    };

    const handleCleanupQueue = async () => {
        setCleanupLoading(true);
        try {
            const result = await cleanupTaskQueue();
            if (result && result.deletedCount !== undefined) {
                setAlertMessage(`Successfully cleaned up ${result.deletedCount} pending tasks`);
                setAlertSeverity('success');
                await Promise.all([loadQueueStatus(), loadTasks()]);
            } else {
                setAlertMessage('Failed to cleanup task queue');
                setAlertSeverity('error');
            }
        } catch (error) {
            console.error('Error cleaning up queue:', error);
            const errorMsg = await createUserFriendlyErrorMessage('cleanup task queue', error);
            setAlertMessage(errorMsg);
            setAlertSeverity('error');
        } finally {
            setCleanupLoading(false);
        }
    };

    const refreshData = async () => {
        await Promise.all([loadQueueStatus(), loadTasks()]);
    };

    const handleTaskCreated = async () => {
        setAlertMessage('Tasks created successfully');
        setAlertSeverity('success');
        await refreshData();
    };

    const handleViewMatch = (matchId: string) => {
        setSelectedMatchId(matchId);
        setViewMatchModalOpen(true);
    };

    const handleCloseViewModal = () => {
        setViewMatchModalOpen(false);
        setSelectedMatchId(null);
    };

    useEffect(() => {
        loadQueueStatus();
    }, [timeRange]);

    useEffect(() => {
        loadTasks();
    }, [timeRange, currentPage]);

    const getStatusChip = (status: string) => {
        const statusConfig = {
            PENDING: { color: 'warning' as const, icon: <Pending /> },
            IN_PROGRESS: { color: 'info' as const, icon: <PlayArrow /> },
            COMPLETED: { color: 'success' as const, icon: <CheckCircle /> },
            FAILED: { color: 'error' as const, icon: <Error /> }
        };
        
        const config = statusConfig[status as keyof typeof statusConfig];
        return (
            <Chip 
                label={status.replace('_', ' ')}
                color={config.color}
                size="small"
                icon={config.icon}
            />
        );
    };

    const truncateText = (text: string | undefined, maxLength: number = 50) => {
        if (!text) return '';
        return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
    };

    const StatusCard: React.FC<{
        title: string;
        count: number;
        icon: React.ReactNode;
        color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
    }> = ({ title, count, icon, color }) => (
        <Grid item xs={12} sm={6} md={3}>
            <Card>
                <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                        {icon}
                        <Typography variant="h6" component="div" ml={1}>
                            {title}
                        </Typography>
                    </Box>
                    <Typography variant="h3" component="div" color={`${color}.main`}>
                        {count}
                    </Typography>
                    <Chip 
                        label={`${count} ${title.toLowerCase()}`}
                        color={color}
                        size="small"
                        sx={{ mt: 1 }}
                    />
                </CardContent>
            </Card>
        </Grid>
    );

    const selectedRange = TIME_RANGES.find(range => range.hours === timeRange);

    return (
        <Box p={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box>
                    <Typography variant="h4" component="h1">
                        Flash Bet Tasks
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mt={1}>
                        Showing data for: {selectedRange?.label || `${timeRange} hours`}
                    </Typography>
                </Box>
                <Box display="flex" gap={2} alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Time Range</InputLabel>
                        <Select
                            value={timeRange}
                            label="Time Range"
                            onChange={(e) => handleTimeRangeChange(e.target.value as number)}
                            startAdornment={<AccessTime sx={{ mr: 1 }} />}
                        >
                            {TIME_RANGES.map((range) => (
                                <MenuItem key={range.hours} value={range.hours}>
                                    {range.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button
                        variant="outlined"
                        onClick={refreshData}
                        disabled={loading || tasksLoading}
                        startIcon={loading || tasksLoading ? <CircularProgress size={20} /> : <Refresh />}
                    >
                        Refresh
                    </Button>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={handleCleanupQueue}
                        disabled={cleanupLoading || !queueStatus || queueStatus.pending === 0}
                        startIcon={cleanupLoading ? <CircularProgress size={20} /> : <CleaningServices />}
                    >
                        Cleanup Queue
                    </Button>
                </Box>
            </Box>

            {alertMessage && (
                <Alert 
                    severity={alertSeverity} 
                    onClose={() => setAlertMessage('')}
                    sx={{ mb: 3 }}
                >
                    {alertMessage}
                </Alert>
            )}

            <Typography variant="body2" color="text.secondary" mb={2}>
                Last updated: {lastUpdated.toLocaleString()}
            </Typography>

            <Typography variant="h6" component="h2" mb={3}>
                Task Queue Status
            </Typography>

            {loading && !queueStatus ? (
                <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                </Box>
            ) : (
                <Grid container spacing={3} mb={4}>
                    <StatusCard
                        title="Pending"
                        count={queueStatus?.pending || 0}
                        icon={<Pending color="warning" />}
                        color="warning"
                    />
                    <StatusCard
                        title="In Progress"
                        count={queueStatus?.inProgress || 0}
                        icon={<PlayArrow color="info" />}
                        color="info"
                    />
                    <StatusCard
                        title={`Completed (${selectedRange?.label || `${timeRange}h`})`}
                        count={queueStatus?.completed || 0}
                        icon={<CheckCircle color="success" />}
                        color="success"
                    />
                    <StatusCard
                        title={`Failed (${selectedRange?.label || `${timeRange}h`})`}
                        count={queueStatus?.failed || 0}
                        icon={<Error color="error" />}
                        color="error"
                    />
                </Grid>
            )}

            <Typography variant="h6" component="h2" mb={3}>
                Tasks Details
            </Typography>

            <Card sx={{ mb: 4 }}>
                <CardContent sx={{ p: 0 }}>
                    <TableContainer component={Paper} elevation={0}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>BetsAPI ID</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Sport</TableCell>
                                    <TableCell>Category</TableCell>
                                    <TableCell>Created</TableCell>
                                    <TableCell>Last Updated</TableCell>
                                    <TableCell>Error Message</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tasksLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">
                                            <CircularProgress />
                                        </TableCell>
                                    </TableRow>
                                ) : tasks && tasks.content.length > 0 ? (
                                    tasks.content.map((task) => (
                                        <TableRow key={task.id}>
                                            <TableCell>
                                                <Typography variant="body2" fontFamily="monospace">
                                                    {task.betsApiId}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {getStatusChip(task.status)}
                                            </TableCell>
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Avatar 
                                                        src={task.sport.photoUrl} 
                                                        sx={{ width: 24, height: 24 }}
                                                    >
                                                        {task.sport.name.charAt(0)}
                                                    </Avatar>
                                                    <Typography variant="body2">
                                                        {task.sport.name}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <Avatar 
                                                        src={task.category.photoUrl ?? ""} 
                                                        sx={{ width: 24, height: 24 }}
                                                    >
                                                        {task.category.name.charAt(0)}
                                                    </Avatar>
                                                    <Typography variant="body2">
                                                        {task.category.name}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {new Date(task.createdAt).toLocaleString()}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {new Date(task.lastStatusChangeAt).toLocaleString()}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {task.errorMsg ? (
                                                    <Tooltip title={task.errorMsg} arrow>
                                                        <Typography 
                                                            variant="body2" 
                                                            color="error"
                                                            sx={{ cursor: 'help' }}
                                                        >
                                                            {truncateText(task.errorMsg, 30)}
                                                        </Typography>
                                                    </Tooltip>
                                                ) : (
                                                    <Typography variant="body2" color="text.secondary">
                                                        -
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Box display="flex" gap={1}>
                                                    {task.status === 'PENDING' && (
                                                        <Tooltip title="Delete Task">
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => handleDeleteTask(task.id)}
                                                                disabled={actionLoading[task.id]}
                                                            >
                                                                {actionLoading[task.id] ? (
                                                                    <CircularProgress size={16} />
                                                                ) : (
                                                                    <Delete />
                                                                )}
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {task.status === 'FAILED' && (
                                                        <Tooltip title="Retry Task">
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={() => handleRetryTask(task.id)}
                                                                disabled={actionLoading[task.id]}
                                                            >
                                                                {actionLoading[task.id] ? (
                                                                    <CircularProgress size={16} />
                                                                ) : (
                                                                    <Refresh />
                                                                )}
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {task.status === 'COMPLETED' && task.matchId && (
                                                        <Tooltip title="View Match">
                                                            <IconButton
                                                                size="small"
                                                                color="info"
                                                                onClick={() => handleViewMatch(task.matchId!)}
                                                            >
                                                                <Visibility />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">
                                            <Typography color="text.secondary">
                                                No tasks found for the selected time range
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    
                    {tasks && tasks.totalPages > 1 && (
                        <Box display="flex" justifyContent="center" p={2}>
                            <Pagination
                                count={tasks.totalPages}
                                page={currentPage + 1}
                                onChange={handlePageChange}
                                color="primary"
                                showFirstButton
                                showLastButton
                            />
                        </Box>
                    )}
                </CardContent>
            </Card>

            <Card>
                <CardContent>
                    <Typography variant="h6" component="h3" mb={2}>
                        About Match Import Tasks
                    </Typography>
                    <Typography variant="body1" paragraph>
                        This page shows the current status of match import tasks from BetsApi. Tasks are processed 
                        automatically in the background to import match data, team information, and events.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        • <strong>Pending:</strong> Tasks waiting to be processed<br/>
                        • <strong>In Progress:</strong> Tasks currently being processed<br/>
                        • <strong>Completed:</strong> Tasks successfully completed in the selected time range<br/>
                        • <strong>Failed:</strong> Tasks that failed in the selected time range<br/>
                    </Typography>
                    <Box mt={2}>
                        <Typography variant="body2" color="text.secondary">
                            <strong>Actions:</strong> Use the delete button (🗑️) to remove pending tasks, or the retry button (🔄) to retry failed tasks.
                            The "Cleanup Queue" button removes all pending tasks from the queue.
                        </Typography>
                    </Box>
                </CardContent>
            </Card>

            {/* Floating Action Button */}
            <Fab
                color="primary"
                sx={{
                    position: 'fixed',
                    bottom: 16,
                    right: 16,
                    zIndex: 1000
                }}
                onClick={() => setAddTaskModalOpen(true)}
            >
                <Add />
            </Fab>

            {/* Add Task Modal */}
            <AddTaskModal
                open={addTaskModalOpen}
                onClose={() => setAddTaskModalOpen(false)}
                onSuccess={handleTaskCreated}
            />

            {/* View Match Modal */}
            {/* View Match Modal */}
            <ViewMatchModal
                open={viewMatchModalOpen}
                onClose={handleCloseViewModal}
                matchId={selectedMatchId}
            />
        </Box>
    );
};

export default FlashBetTasksPage;